import { Chip } from "@mui/material"
import { isTrialTerm, licenceStatus, type Licence, type LicenceStatus } from "@xop/dashboard-shared"

/**
 * The one place a licence's term becomes a colour and a word.
 *
 * `licenceStatus` itself lives in `@xop/dashboard-shared` so the chip here, the
 * badge on a customer and the reseller portal's own list cannot disagree about
 * the same licence. This file only decides how it LOOKS.
 */

const PRESENTATION: Record<
  LicenceStatus,
  { label: string; color: "error" | "warning" | "success" | "default" }
> = {
  expired: { label: "Expired", color: "error" },
  expiring: { label: "Expiring", color: "warning" },
  active: { label: "Active", color: "success" },
  // Not "never expires" — the word people look for on a licence list is the one
  // that appears on the contract.
  perpetual: { label: "Perpetual", color: "default" },
}

export function LicenceStatusChip({ licence }: { licence: Pick<Licence, "expiresOn"> }) {
  const { label, color } = PRESENTATION[licenceStatus(licence)]

  return <Chip size="small" label={label} color={color} variant={color === "default" ? "outlined" : "filled"} />
}

/**
 * The WORKFLOW — the third axis, and the one the CTO reads. A 30-day trial
 * needs nobody; anything longer waits for approval, and the file does not exist
 * until it comes. "Needs approval" is the state BEFORE anyone has asked: the
 * terms are longer than a trial and no request has been sent yet.
 */
export function LicenceWorkflowChip({ licence }: { licence: Pick<Licence, "expiresOn" | "approval" | "trial"> }) {
  const { status } = licence.approval
  if (status === "pending") return <Chip size="small" color="warning" variant="outlined" label="Awaiting approval" />
  if (status === "approved") return <Chip size="small" color="success" variant="outlined" label="Approved" />
  if (status === "rejected") return <Chip size="small" color="error" label="Rejected" />
  if (isTrialTerm(licence.expiresOn, licence.trial.windowEndsOn)) {
    return <Chip size="small" color="info" variant="outlined" label="Trial" />
  }
  // A WARNING badge, not a neutral tag: this licence cannot produce a file
  // until someone acts.
  return <Chip size="small" color="warning" label="Needs approval" />
}

/**
 * RENEWAL, kept light (2026-09-15): "Renewal ready" while crm-api's window is
 * open and nothing has been drafted; "Next: XOP-9-1" once a successor has
 * been generated. Nothing in between — the builder says the rest.
 */
export function LicenceRenewalChip({ licence }: { licence: Pick<Licence, "renewal"> }) {
  const renewal = licence.renewal
  if (!renewal) return null
  const successor = renewal.successor
  if (successor?.status === "issued" && successor.serial) {
    return <Chip size="small" color="success" variant="outlined" label={`Next: ${successor.serial}`} data-testid="licence-renewal-next" />
  }
  if (renewal.renewable && !successor) {
    return <Chip size="small" color="secondary" variant="outlined" label="Renewal ready" data-testid="licence-renewal-ready" />
  }
  return null
}
